import styled, { keyframes } from 'styled-components';
import { rgba } from 'polished';
import colors from 'constants/colors';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100002;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 16px 16px;
  background: ${rgba(colors.main.black, 0.55)};
  animation: ${fadeIn} 0.15s ease-out;

  @media screen and (max-width: 992px) {
    padding: 8vh 12px 12px;
  }
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 640px;
  max-width: 100%;
  max-height: 70vh;
  overflow: hidden;
  background: ${colors.main.white};
  border-radius: 12px;
  box-shadow: 0 16px 70px ${rgba(colors.main.black, 0.35)};
  animation: ${slideIn} 0.15s ease-out;

  @media screen and (max-width: 992px) {
    max-height: 78vh;
  }
`;

export const SearchRow = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.main.dividers};
  color: ${colors.main.grey};
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 16px;
  color: ${colors.main.black};
  background: transparent;

  &::placeholder {
    color: ${colors.main.placeholdersAndDisabledBtnsText};
  }
`;

export const Kbd = styled.kbd`
  flex-shrink: 0;
  padding: 3px 7px;
  font-family: inherit;
  font-size: 11px;
  color: ${colors.main.grey};
  background: ${colors.main.secondaryBtns};
  border: 1px solid ${colors.main.dividers};
  border-radius: 4px;
`;

export const Results = styled.div`
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 0 12px;
`;

export const GroupLabel = styled.div`
  padding: 12px 20px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.main.grey};
`;

export const ResultButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 20px;
  border: none;
  outline: none;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  background: ${({ $active }) => ($active ? colors.main.secondaryBtns : 'transparent')};
`;

export const Thumb = styled.img`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  background: ${colors.main.dividers};
`;

export const IconTile = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  color: ${colors.main.blue};
  background: ${rgba(colors.main.lightBlue, 0.12)};

  img,
  svg {
    width: 22px;
    height: 22px;
  }
`;

export const AwardTile = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  font-size: 20px;
  background: ${rgba(colors.main.brown, 0.15)};

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 34px;
    height: 34px;
  }
`;

export const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ItemTitle = styled.span`
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.main.black};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ItemSubtitle = styled.span`
  overflow: hidden;
  font-size: 13px;
  color: ${colors.main.grey};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const ItemMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  font-size: 12px;
  color: ${colors.main.grey};
  white-space: nowrap;
`;

export const MetaCheck = styled.span`
  font-weight: 700;
  color: ${colors.main.green};
`;

export const MetaPictogram = styled.img`
  width: 16px;
  height: 16px;
`;

export const MetaDivider = styled.span`
  color: ${colors.main.placeholdersAndDisabledBtnsText};
`;

export const TypeBadge = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.main.placeholdersAndDisabledBtnsText};
`;

export const StateNote = styled.div`
  padding: 10px 20px;
  font-size: 13px;
  color: ${colors.main.grey};
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-size: 14px;
  color: ${colors.main.grey};
`;
